import { getInstanceForModif } from "../../../form.ts";
import { IsiBigFloat } from "../../../IsiBigFloat";
import { IIsiDtoColumnTemplate, IIsiGridColumnMovedEventArgs, IIsiGridColumnResizedEventArgs, IIsiGridOnCellEditStartingEventArgs, IIsiGridOnCellUpdatedEventArgs, IIsiGridOnEditCellEndedEventArgs, IIsiGridOnGetMultiEditorEventArgs, IIsiGridOnRowEditStartingEventArgs, IIsiGridGroupedColumnsChangedEventArgs, IIsiGridColumnSortedEventArgs, IIsiGridOnEditorKeyDownEventArgs, IIsiGridCheckCanEditCell, IIsiGridRowDragStartArgs } from "../../../IsiWebGUI/IsiFW/Components/Grid/IsiGridBase";
import { IIsiGridViewCellClickingEventArgs, IIsiGridViewDataBoundEventArgs, IIsiGridViewTooltipShowingArgs, IsiGridView } from "../../../IsiWebGUI/IsiFW/Components/Grid/IsiGridView";
import { MultiEditorInfosBase } from "../../../IsiWebGUI/IsiFW/Components/Grid/MultiEditor/MultiEditorInfosBase";
import { IIsiUIFlowResult } from "../../Core/IIsiUIFlowResult";
import { IIsiUIFlowResultValidation } from "../../Core/IIsiUIFlowResultValidation";
import { IsiActionContext } from "../../Core/IsiActionContext";
import { IsiPlColRacc } from "../../PresentationLogic/IsiPlColRacc";
import { IsiPlInstanceScreen } from "../../PresentationLogic/IsiPlInstanceScreen";
import { IsiIWSAssociatedViewContext } from "./IsiIwsAssociatedViewContext";
import { IIsiAssociatedViewValidator } from "./Validator/IIsiAssociatedViewValidator";
import { IIsiDtoGridColumn } from "../../../Dtos/Isilog.IsiComponent.IsiDataTransferObject";

/**
 * Réprésente une donnée associée. Cette classe est héritable par DA pour écrire du code spécifique
 **/
export class IsiIWSAssociatedView {
    /**
     * Fonction de récupération du template d'une colonne
     * @param columnKey
     */
    getColumnTemplate(columnKey: string): IIsiDtoColumnTemplate {
        return this.getColumnTemplateOverridable(columnKey);
    }
    /**
     * A surchager pour définir des templates sur des colonnes
     * @param columnKey
     */
    getColumnTemplateOverridable(columnKey: string): IIsiDtoColumnTemplate {
        return null;
    }

    /**
     * Permet de surcharger la largeur d'une colonne
     * @param column
     * @returns
     */
    getNewColumnWidth(column: IIsiDtoGridColumn): string {
        return this.getNewColumnWidthOverridable(column);
    }

    /**
     * Permet de surcharger la largeur d'une colonne
     * @param column
     * @returns
     */
    getNewColumnWidthOverridable(column: IIsiDtoGridColumn): string {
        const gridView = uiCore.componentManager.getComponent<IsiGridView>(this.context.gridViewId);
        if (uiCore.isRWDPhone() && gridView.getDto().forceTabletRenderMode && parseInt(column.width) >= 201) {
            return (parseInt(column.width) * (2/3)).toString();
        } else {
            return column.width;
        }
    }

    private _context: IsiIWSAssociatedViewContext;
    /**
     * Validateur spécifique ou non
     * */
    private _validator: IIsiAssociatedViewValidator;

    constructor(associatedViewContext: IsiIWSAssociatedViewContext) {
        this._context = associatedViewContext;
        this._editorsInfos = [];
    }
    /**
     * Classe de validation d'une modification d'une da
     * */
    protected set validator(validator: IIsiAssociatedViewValidator) {
        this._validator = validator;
    }


    protected get context() {
        return this._context;
    }

    private _editorsInfos: Object[];

    protected get editorsInfos() {
        return this._editorsInfos;
    }

    /**
     * S'assure qu'il y a au moins une ligne de sélectionnée dans la grille
     * @param errorMessage true|false pour afficher ou non le message standard; une string pour un message personnalisé (true par défaut)
     */
    protected ensureAtLeastOneSelectedRow(errorMessage?: boolean | string) {
        if (typeof (errorMessage) === "undefined") errorMessage = true;
        if (screenCore.gridManager.checkDetailGridValidity(this.context)) {
            return true;
        } else {
            if (errorMessage === true) {
                uiCore.showErrorMessage(uiCore.getMessage("Err", "Grid_NoSelection").IsiLabel);
            } else {
                uiCore.showErrorMessage(errorMessage);
            }
            return false;
        }
    }

    /**
     * Indique s'il y a des lignes dans la grille
     */
    protected hasRows(): boolean {
        return this._context.hasRows;
    }


    //#region "Gestion des actions"

    //#region "Dissociation"

    /**
     * Avant la dissociation.
     * */
    beforeDissociate(assContext: IsiIWSAssociatedViewContext): Promise<IIsiUIFlowResult> {

        return new Promise((resolve, reject) => {
            this.beforeDissociateOverridable(assContext).then((flow) => {
                resolve(flow);
            });
        });
    }
    /**
     * Méthode surchargeable avant la dissociation.
     * */
    protected beforeDissociateOverridable(assContext: IsiIWSAssociatedViewContext): Promise<IIsiUIFlowResult> {
        return new Promise((resolve, reject) => {
            resolve({
                continue: true,
                result: true
            });
        });
    }
    //#endregion "Dissociation"

    //#region "Choix de l'instance"

    /**
     * Traitement avant le choix de l'instance permet de cancel le choix.
     **/
    beforeChooseInstance(): Promise<boolean> {
        return this.beforeChooseInstanceOverridable();
    }

    /**
    * Traitement surchargeable avant le choix de l'instance.
    * @returns true pour le choix d'instance standard ou false sinon
    **/
    protected beforeChooseInstanceOverridable(): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            resolve(true);
        })
    }
    /**
    * Traitement avant le choix de l'instance permet de cancel le choix.
    * Dans le cas de l'ouverture de la popin de choix
    **/
    promptChooseInstance(callbackFn: Function) {
        return this.promptChooseInstanceOverridable(callbackFn);
    }

    /**
    * Traitement surchargeable avant le choix de l'instance.
    * Dans le cas de l'ouverture de la popin de choix
    * @returns true pour le choix d'instance standard ou false sinon
    **/
    protected promptChooseInstanceOverridable(callbackFn: Function) {

        const plInstanceScreen = new IsiPlInstanceScreen;
        plInstanceScreen.GetNbDroitModif(this.context.name, null).then((result) => {
            if (result.reponse > 1) {
                uiCore.setSubmit(false);
                uiCore.addUITreatment({
                    func: getInstanceForModif(this.context.name, "AssociationInstance", null),
                    callback: callbackFn
                });
            } else {
                callbackFn();
            }
        })
    }

    /**
     * Traitement après le choix de l'instance
     **/
    afterChooseInstance(actionContext: IsiActionContext): boolean {
        return this.afterChooseInstanceOverridable(actionContext);
    }

    /**
     * Traitement surchargeable après le choix de l'instance.
     **/
    protected afterChooseInstanceOverridable(actionContext: IsiActionContext) {
        return true;
    }


    /**
     * Traitement avant exécution de l'action demandée
     **/
    beforeActionOnDetail(actionContext: IsiActionContext): Promise<IIsiUIFlowResult> {
        let result = this.beforeActionOverridable(actionContext);
        if (!(result instanceof Promise)) {
            return Promise.resolve({ result: true, continue: actionContext.beforeActionOnDetail });
        } else {
            return result;
        }
    }

    /**
     * Traitement surchargeable avant exécution de l'action demandée
     **/
    protected beforeActionOverridable(actionContext: IsiActionContext): Promise<IIsiUIFlowResult> | void {
        actionContext.beforeActionOnDetail = true;
        return Promise.resolve({ result: true, continue: actionContext.beforeActionOnDetail });
    }
    /**
     * Traitement avant la validation
     **/
    beforeValidate(datas): Promise<IIsiUIFlowResultValidation> {

        if (this._validator) {
            return this._validator.beforeValidate(datas);
        } else {
            return Promise.resolve({
                result: true,
                continue: true,
                postBack: true,
                refresh: false,
                additionnalInformation: null
            });
        }

    }

    /**
     * Déclanche une action avant le double click sur des lignes selectionnées
     * @param actionContext
     * @returns
     */
    beforeRowDblClick(actionContext: IsiActionContext): boolean {
        return this.beforeRowDblClickOverridable(actionContext);
    }
    /**
     * Déclanche une action avant le double click sur des lignes selectionnées (surchargable)
     * @param actionContext
     * @returns
     */
    protected beforeRowDblClickOverridable(actionContext: IsiActionContext): boolean {
        return true;
    }


    /**
     * Affiche le message d'erreur lors de la sauvegarde d'une modification
     * */
    showSaveChangesError(error: string) {
        uiCore.showErrorMessage(error);
    }

    /**
     * Simule l'ouverture d'un menu
     * @param group
     * @param action
     */
    protected openMenu(group: string, action: string): void {
        const idBar: string = this.context.id + MNU_DETAIL;
        const actionContext = new IsiActionContext();
        const id = idBar + MNU_SEP + group + MNU_SEP + action;
        const entry = uiCore.menuManager.getEntryById(id);
        actionContext.event = undefined;
        actionContext.menu = entry;
        actionContext.menuId = id;
        actionContext.associatedViewContext = this.context;
        screenCore.uiActionClicked(actionContext);
    }

    //#endregion    "Choix de l'instance"

    //#endregion "Gestion des actions"


    /**
     * Traitement sur le clic sur une cellule
     * */
    cellClicking(args: IIsiGridViewCellClickingEventArgs) {

        if (args.column.columnRacc && args.column.columnRacc !== "-1") {
            //YLAR PB0019797 : Correction lancement de raccourci dans la grille.
            if (args.cellValue && args.cellValue.trim() != "") {

                let colRaccPl: IsiPlColRacc = new IsiPlColRacc();

                colRaccPl.GetColRacc(this.context.id, args.columnKey).then((raccourci) => {
                    if (raccourci) {
                        executeRaccourci(("" + args.cellValue).replace("'", "''"), ("" + raccourci).replace("'", "''"));
                    }
                });
            }
        }

        this.cellClickingOverridable(args);
    }

    /**
     * Traitement surchargeable sur le clic sur une ligne
     * @param args Les arguments de la ligne où le click a été effectué
     */
    protected cellClickingOverridable(args: IIsiGridViewCellClickingEventArgs) {
        args.cancel = false;
    }

    /**
     * Traitement sur le double-clic sur une ligne
     * @param gridName Le nom de la grille
     * @param cellId L'identifiant de la cellule
     */
    cellDoubleClicked(cellId: string) {
        return this.cellDoubleClickedOverridable(cellId);
    }

    /**
     * Traitement surchargeable sur le double-clic sur une ligne
     * @param gridName Le nom de la grille
     * @param cellId L'identifiant de la cellule
     */
    protected cellDoubleClickedOverridable(cellId: string) {
        return true;
    }

    /**
     * Fonction appelee après la modification d'une cellule
     **/
    cellUpdated(args: IIsiGridOnCellUpdatedEventArgs) {


        //Si la longueur de la valeur est supérieure à celle du champ en base
        if (args.cellValue && args.cellValue.length > args.column.fieldLength && args.column.dataType !== "date") {
            args.error = true;
            uiCore.showErrorMessage(uiCore.getMessage("Err", "Data_Trunc").IsiLabel);
        } else {
            if (args.column.dataType === "number" && args.cellValue !== "") {
                this.checkNumberFormat(args,"E");
            }

            this.cellUpdatedOverridable(args);

            if (!args.error) {
                //On change le style du bouton enregistrer (si il est présent)
                this.changeValidationState(true);
            }
        }
    }

    /**
     * Fonction appelée après avoir tapé sur une touche dans la cellule.
     **/
    editorKeyDown(args: IIsiGridOnEditorKeyDownEventArgs) {
        this.editorKeyDownOverridable(args)
    }


    /**
     * Fonction appelee après l'edition d'une cellule
     **/
    editCellEnded(args: IIsiGridOnEditCellEndedEventArgs) {
        this.editCellEndedOverridable(args);
    }

    /**
     * Fonction appelee après l'edition d'une cellule
     **/
    protected editCellEndedOverridable(args: IIsiGridOnEditCellEndedEventArgs) {
    }

    /**
     * Evenement lors du resize d'une colonne
     * @param args
     */
    columnResized(args: IIsiGridColumnResizedEventArgs) {
        this.columnResizedOverridable(args);
    }

    /**
     * Evenement surchargeable lors du resize d'une colonne
     * @param args
     */
    protected columnResizedOverridable(args: IIsiGridColumnResizedEventArgs) {
    }

    /**
     * Evenement lors du déplacement d'une colonne
     * @param args
     */
    columnMoved(args: IIsiGridColumnMovedEventArgs) {
        this.columnMovedOverridable(args);
    }

    /**
     * Evenement surchargeable lors du déplacement d'une colonne
     * @param args
     */
    protected columnMovedOverridable(args: IIsiGridColumnMovedEventArgs) {
    }

    /**
    * Evenement lors du tri d'une colonne
     * @param args
     */
    columnSorted(args: IIsiGridColumnSortedEventArgs) {
        this.columnSortedOverridable(args);
    }

    /**
     * Evenement surchargeable lors du tri d'une colonne
     * @param args
     */
    protected columnSortedOverridable(args: IIsiGridColumnSortedEventArgs) {
    }

    /**
     * Evenement lors d'une modif du groupement des colonnes
     * @param args
     */
    groupedColumnsChanged(sender: IsiGridView, args: IIsiGridGroupedColumnsChangedEventArgs) {
        this.groupedColumnsChangedOverridable(sender, args);
    }

    /**
     * Evenement surchargeable lors d'une modif du groupement des colonnes
     * @param args
     */
    protected groupedColumnsChangedOverridable(sender: IsiGridView, args: IIsiGridGroupedColumnsChangedEventArgs) {
    }

    /**
     * Permet de vérifier la saisie pour des colonnes/editeurs de type numérique
     */
    protected checkNumberFormat(args: IIsiGridOnCellUpdatedEventArgs,typeNum : string) {

        //Permet de récupérer les valeurs max et min que l'on peut saisir dans une cellule de type numeric
        let minMaxValue: string = "";
        for (var i = 0; i < 254; i++) {
            minMaxValue += "9";
        }

        let minValue = "-" + minMaxValue;
        let maxValue = minMaxValue;
        //Récupère le nb de décimales
        let nbDecimal = getNbDecimal(args.cellValue, typeNum);

        //Permet de vérifier le format et la gestion des erreurs s'il y'en a
        if (!iChkFtByValue(args.cellValue, typeNum, args.column.fieldLength, nbDecimal, minValue, maxValue)) {
            args.error = true;
        } else {
            //Sinon on formate la valeur pour la récupérer en type numeric
            var bigFloat = new IsiBigFloat(args.cellValue);
            let formatedValue = window.isiGetNumber(bigFloat.getValue());
            args.cellValue = formatedValue;
        }
    }

    /**
     * Change l'état du bouton d'enregistrement de la grille.
     * 
     * */
    public changeValidationState(modif: boolean) {
        jQuery("[id*='DATA_VALIDATE_'][id*='" + this.context.gridViewId + "']").each(function () {
            let currentElement = jQuery(this);
            if (!currentElement.hasClass("disabled")) {
                if (modif) {
                    if (!currentElement.hasClass(MODIFICATION)) {
                        // Uniquement si l'élément n'est pas 'désactivé'
                        currentElement.addClass(MODIFICATION);
                    }
                } else {
                    if (currentElement.hasClass(MODIFICATION)) {
                        // supprime le style de modification
                        currentElement.removeClass(MODIFICATION);
                    }
                }
            }
        });
    }
    /**
     * Fonction appelee après la modification d'une cellule
     **/
    protected cellUpdatedOverridable(args: IIsiGridOnCellUpdatedEventArgs) {
    }

    /**
    * Fonction appelée après avoir tapé une touche dans la cellule
    **/
    protected editorKeyDownOverridable(args: IIsiGridOnEditorKeyDownEventArgs) {
    }



    /** 
     * Retourne l'éditeur d'une colonne
     */
    getMultiEditor(args: IIsiGridOnGetMultiEditorEventArgs) {
        args.editor = this.editorsInfos[args.columnName];
    }

    /** 
     * Traitement après sauvegarde
     */
    afterSaveChanges(datas) {
        this.afterSaveChangesOverridable(datas);
    }

    /**
     * Traitement surchargeable après sauvegarde
     */
    protected afterSaveChangesOverridable(datas) {
    }

    /**
    * Fonction appelée avant l'édition d'une cellule.
    **/
    cellEditStarting(args: IIsiGridOnCellEditStartingEventArgs) {
        const checkCellEditArgs: IIsiGridCheckCanEditCell = {
            cellKey: args.cellKey,
            datas: args.datas
        }
        if (this.checkCanEditCell(checkCellEditArgs)) {
            this.cellEditStartingOverridable(args);
        } else {
            args.cancel = true;
        }
    }
    /**
    *(Surchargeable) Fonction appelée avant l'édition d'une cellule.
    * l'élement data de args contient les informations de la ligne de la cellule.
    **/
    protected cellEditStartingOverridable(args: IIsiGridOnCellEditStartingEventArgs) {
    }

    /**
     * Fonction qui permet de vérifier si une cellule peut être édité.
     * @param args
     * @returns
     */
    public checkCanEditCell(args: IIsiGridCheckCanEditCell) {
        return this.canEditCellOverridable(args);
    }

    /**
     * Fonction surchargeable qui permet de d'indiquer si une cellule peut être modifier.
     * Elle permet également d'empêcher le lancement de l'édition d'une cellule
     * @param args
     * @returns
     */
    protected canEditCellOverridable(args: IIsiGridCheckCanEditCell) {
        return true;
    }

    /**
    * Fonction appelée avant l'édition d'une ligne permet notamment d'interdire la saisie des cellules d'une ligne (avant l'affichage des éditeurs).
    **/
    rowEditStarting(args: IIsiGridOnRowEditStartingEventArgs) {
        this.rowEditStartingOverridable(args);
    }
    /**
    *(Surchargeable) Fonction appelée avant l'édition d'une ligne permet notamment d'interdire la saisie des cellules d'une ligne (avant l'affichage des éditeurs).
    * Pour annuler la modification renseigner le cancel=true sur l'args.
    * l'élement data de args contient les informations de la ligne de la cellule.
    **/
    protected rowEditStartingOverridable(args: IIsiGridOnRowEditStartingEventArgs) {
    }

    /**
     * Fonction appelée à l'affichqage d'une tooltip sur une cellule
     * @param args
     */
    tooltipShowing(args: IIsiGridViewTooltipShowingArgs) {
        this.tooltipShowingOverridable(args);
    }

    /**
    * (Surchargeable) Fonction appelée à l'affichqage d'une tooltip sur une cellule.
    * L'élement data de args contient les informations de la ligne de la cellule.
    * Pour modifier le tooltip, il suffit de modifier l'attribut tooltip de args.
    * @param args
    */
    protected tooltipShowingOverridable(args: IIsiGridViewTooltipShowingArgs) {
    }

    /**
    * Fonction appelée lorsque les données sont chargées dans la das.
    * @param args
    */
    databound(args: IIsiGridViewDataBoundEventArgs) {
        this.databoundOverridable(args);
    }
    /**
     * Fonction surchargeable permettant d'éffectuer des traitements post chargement des données d'une da.
     * @param args
     */
    protected databoundOverridable(args: IIsiGridViewDataBoundEventArgs) {
    }

    /**
     * Enregistre l'éditeur pour une colonne
     * */
    protected registerEditorInfos(col: string, editor: MultiEditorInfosBase) {
        this.editorsInfos[col] = editor;
    }

    /**
     * Action exécutée après un monter/descendre
     * @returns
     */
    public afterMoveItem(): Promise<IIsiUIFlowResult> {
        return this.afterMoveItemOverridable()
    }

    /**
     * Action exécutée après un monter/descendre
     * @returns
     */
    protected async afterMoveItemOverridable(): Promise<IIsiUIFlowResult> {
        return { continue: true, result: true };
    }


    /**
     * Evenement déclenché après le rendu des lignes de la grille
     */
    rowsRendered() {
        this.rowsRenderedOverridable();
    }

    /**
     * Evenement surchargeable déclenché après le rendu des lignes de la grille
     */
    protected rowsRenderedOverridable() {
    }

    /**
     * Evenement déclenché après le début du drag d'une ligne de la grille
    */
    rowDragStart(args: IIsiGridRowDragStartArgs) {
        this.rowDragStartOverridable(args);
    }

    /**
     * Evenement surchargeable déclenché après le début du drag d'une ligne de la grille
     */
    protected rowDragStartOverridable(args: IIsiGridRowDragStartArgs) {
    }

}
